import React, { useState } from 'react';
import { ExternalLink } from '../router';
import { BOOKING_URL, CONTACT_EMAIL } from '../links';
import GhlFormModal, { GHL_FORMS } from '../components/GhlFormModal';
import SquareOfHealth from '../components/SquareOfHealth';

// One-page offer for tech companies and coworking spaces, at /office. Shared as a direct link; not in the main menu.
// Written for the buyer (engineering lead or HR), not the employee.

const evidence = [
  {
    figure: '22%',
    text: 'of developers and engineering leaders report critical levels of burnout. Only 21% are fully healthy.',
    source: 'LeadDev Engineering Leadership Report, 2025',
    href: 'https://leaddev.com/culture/engineering-burnout-rising-2025-layoffs-reshape-tech-industry',
  },
  {
    figure: '2.6×',
    text: 'Burned-out employees are 2.6 times as likely to be looking for another job, and 63% more likely to take a sick day.',
    source: 'Gallup, 7,500 employees',
    href: 'https://www.gallup.com/workplace/237059/employee-burnout-part-main-causes.aspx',
  },
  {
    figure: '£4.70',
    text: 'average return for every £1 employers invest in the mental health and wellbeing of their people.',
    source: 'Deloitte UK, 2024',
    href: 'https://www.deloitte.com/uk/en/about/press-room/poor-mental-health-costs-uk-employers-51-billion-a-year-for-employees.html',
  },
];

const pillars = [
  ['Physical', 'screen posture, neck and back strain, eye fatigue, movement and sleep.'],
  ['Biochemical', 'the food, hydration and minerals behind the 2pm crash, with insight from blood work.'],
  ['Energy', 'a nervous system under constant load, and how to bring it back to baseline.'],
  ['Mental', 'focus, context switching, decision fatigue and problem solving under pressure.'],
];

const ForCompanies: React.FC = () => {
  const [enquiry, setEnquiry] = useState<null | 'company' | 'coworking'>(null);

  return (
    <>
      {/* Hero: name the buyer and their problem */}
      <section className="section photo-section for-companies-hero" style={{ backgroundImage: 'url(/workshop2.jpg)' }}>
        <div className="container container--narrow">
          <h1>Stop Losing Your Best Engineers to Burnout.</h1>
          <p className="lead">
            If you lead an engineering team or HR, you can see it: slower delivery, more sick days, and your best senior
            engineers burning out.
          </p>
          <p className="lead">
            <strong>Productivity doesn't need another perk. It needs one systemic solution.</strong>
          </p>
          <div className="button-row">
            <ExternalLink href={BOOKING_URL} className="btn btn-primary">
              Book a Planning Call
            </ExternalLink>
            <button className="btn btn-primary" onClick={() => setEnquiry('company')}>
              Send an Enquiry
            </button>
          </div>
        </div>
      </section>

      {/* The buyer's pain, then the after-state */}
      <section className="section section--lavender">
        <div className="container container--narrow">
          <h2>What It Looks Like From Your Seat</h2>
          <ul className="symptoms">
            <li>Deadlines slip, though nobody is working less.</li>
            <li>Your strongest engineers are the most exhausted.</li>
            <li>Sick days and quiet resignations keep creeping up.</li>
            <li>The wellness budget goes on perks that change nothing by Monday.</li>
          </ul>
          <p className="lead">
            Imagine your team after a wellness day: <strong>sharper, not just rested.</strong> Focus that holds through the
            afternoon, fewer sick days, and senior people who stay.
          </p>
        </div>
      </section>

      {/* Why it matters */}
      <section className="section">
        <div className="container">
          <h2 className="center">Why It Matters</h2>
          <p className="center lead evidence-intro">
            Tired engineers still show up. They deliver less, solve less, and quietly start looking elsewhere.
          </p>
          <div className="evidence evidence--three">
            {evidence.map((item) => (
              <div key={item.figure} className="evidence__item">
                <strong>{item.figure}</strong>
                <p>{item.text}</p>
                <ExternalLink href={item.href} className="evidence__source">
                  {item.source}
                </ExternalLink>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The framework */}
      <section className="section section--alt">
        <div className="container container--narrow">
          <h2>The Square of Health for Teams</h2>
          <p>
            Most wellness programs fix one corner and ignore the rest. I work on all four sides of what drives performance, so
            the change holds after the day is over.
          </p>
          <SquareOfHealth />
          <ul className="list">
            {pillars.map(([name, text]) => (
              <li key={name}>
                <strong>{name}:</strong> {text}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* The offer: one partner, customised */}
      <section className="section">
        <div className="container container--narrow">
          <h2>One Partner, Built Around Your Team</h2>
          <p className="lead">
            Every company is different. Some need a wellness day. Some need a coach on call. Some need someone who advises the
            whole team on health, from nutrition to exercise. <strong>You can have all of it, from one person.</strong>
          </p>
          <ul className="symptoms">
            <li>
              <strong>Wellness and mental health days:</strong> practical workshops, on-site or online, from one hour to a full
              day.
            </li>
            <li>
              <strong>An on-call coach for your team:</strong> 1:1 sessions for the people who need support, when they need it.
            </li>
            <li>
              <strong>A holistic health advisor for the team:</strong> nutrition, exercise, sleep, posture and herbal support, in
              one consistent approach.
            </li>
            <li>
              <strong>Any mix of the above,</strong> shaped around your goals.
            </li>
          </ul>
          <p>
            On a short planning call we pinpoint your team's top 3 energy drains and choose the mix that fits.
          </p>
          <ExternalLink href={BOOKING_URL} className="btn btn-primary">
            Book a Planning Call
          </ExternalLink>
        </div>
      </section>

      {/* About Hanna */}
      <section className="intro">
        <div className="container about-me">
          <div className="about-me__text">
            <h2>Who I Am</h2>
            <p>
              I'm Hanna Kinez, an executive performance coach certified across Eastern and Western medicine: applied
              kinesiology, physiotherapy, medical herbalism, CranioSacral therapy, NLP, and MRI and blood work interpretation.
            </p>
            <p>
              For 15 years I've worked with engineers and technical leaders, from global tech companies to small engineering
              teams. Clients have come from <strong>Google, Microsoft, Cisco and NetApp</strong>. I've guided{' '}
              <strong>1,000+ professionals</strong> across <strong>12 countries</strong>, reached <strong>10,000+</strong>{' '}
              through workshops, and spoke to <strong>15,000</strong> at <strong>We Are Developers Berlin</strong>.
            </p>
            <p>My workshops aren't motivational talks. People leave with techniques they can use the same afternoon.</p>
          </div>
          <div className="about-me__photo">
            <img src="/aa8b4378.jpg" alt="Hanna Kinez" />
          </div>
        </div>
      </section>

      {/* Proof */}
      <section className="section">
        <div className="container container--narrow">
          <h2 className="center">What Engineers Say</h2>
          <blockquote className="testimonial">
            <p>
              "I came in skeptical. I am an engineer. What Hanna showed me was not wellness. It was a system. Three months later I
              am clearer than I have been in five years."
            </p>
            <footer>
              — <strong>Jeff J.</strong>, Senior Engineer
            </footer>
          </blockquote>
          <blockquote className="testimonial">
            <p>"I had accepted that I was getting slow because I was growing old. That wasn't the problem."</p>
            <footer>
              — <strong>Marek</strong>, Senior Engineer. Focus went from under 8 minutes to <strong>45+</strong> in five days.
            </footer>
          </blockquote>
        </div>
      </section>

      {/* Coworking spaces */}
      <section className="section section--lavender">
        <div className="container container--narrow">
          <h2>For Coworking Spaces</h2>
          <p>
            Give your members a reason to come in, and a reason to stay. I run <strong>wellness workshops for coworking
            communities</strong>: focus, energy, posture and burnout prevention for the developers, founders and freelancers
            who work at your desks.
          </p>
          <p>
            <em>A single event, or a regular series for your members.</em>
          </p>
          <button className="btn btn-primary" onClick={() => setEnquiry('coworking')}>
            Talk to Me About a Member Series
          </button>
        </div>
      </section>

      {/* Final call to action */}
      <section className="section section--dark">
        <div className="container container--narrow center">
          <h2>Let's Build Your Team's Solution</h2>
          <p>
            A short call to hear about your team and what you want to change. Or write to me at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
          <div className="button-row button-row--center">
            <ExternalLink href={BOOKING_URL} className="btn btn-primary">
              Book a Planning Call
            </ExternalLink>
            <button className="btn btn-primary" onClick={() => setEnquiry('company')}>
              Send an Enquiry
            </button>
          </div>
        </div>
      </section>

      <GhlFormModal
        isOpen={enquiry !== null}
        onClose={() => setEnquiry(null)}
        form={GHL_FORMS.enquiry}
        title={enquiry === 'coworking' ? 'Workshops for Your Members' : 'Wellness Support for Your Team'}
        intro="Tell me a little about your team or space, and I'll reply within 2 working days."
      />
    </>
  );
};

export default ForCompanies;
